import {
  CREDIT_CARD_LIABILITY_TYPE,
  HEM_CONSTANTS,
  INVESTMENT_PROPERTY_INCOME_TYPE,
  MARITAL_STATUSES,
  RENT_EXPENSE_TYPE,
} from "@/constants";
import { toFiniteNumber } from "@/utils/formatNumber";
import { toMonthlyAmount as toMonthlyFrequencyAmount } from "@/utils/frequency";

export interface ServiceabilityIncome {
  amount?: number | string | null;
  frequency?: string | null;
  incomeType?: string | null;
  /** Percentage (0-100) of an investment property income that counts towards serviceability. */
  shading?: number | string | null;
}

export interface ServiceabilityExpense {
  amount?: number | string | null;
  frequency?: string | null;
  expensesType?: string | null;
  /** Percentage (0-100) of the expense carried by this customer. */
  shareOfExpense?: number | string | null;
}

export interface ServiceabilityLiability {
  repaymentAmount?: number | string | null;
  repaymentFrequency?: string | null;
  liabilitiesType?: string | null;
  /** Percentage (0-100) of the credit limit added to a credit card's monthly repayment. */
  buffer?: number | string | null;
  limit?: number | string | null;
  /** Percentage (0-100) of the repayment carried by this customer. */
  shareOfLiability?: number | string | null;
}

export interface ServiceabilityProfile {
  maritalStatus?: string | null;
  numberOfDependants?: number | null;
}

export interface ServiceabilityInput {
  income?: ServiceabilityIncome[] | null;
  expenses?: ServiceabilityExpense[] | null;
  liabilities?: ServiceabilityLiability[] | null;
  customerProfile?: ServiceabilityProfile | null;
  /**
   * Share of the household HEM benchmark carried by this customer, as a fraction of 1.
   * Pass null/undefined (or an empty string, as an unset profile field arrives) to carry the full benchmark.
   */
  shareOfHem?: number | string | null;
}

export interface ServiceabilityResult {
  /** Total monthly net income, after shading. */
  income: number;
  /** Total monthly liability repayments, after each liability's buffer and share. */
  liabilities: number;
  /** Monthly rent after its share, added to totals but excluded from the HEM comparison. */
  rent: number;
  /** Monthly non-rent living expenses after each expense's share. */
  statedHouseholdExpenses: number;
  /** Full HEM benchmark for the household, null when HEM cannot be resolved. */
  hemBenchmark: number | null;
  /** HEM benchmark after the split, null when HEM cannot be resolved. */
  hemApplied: number | null;
  /** Share of the HEM benchmark applied to this customer, as a fraction of 1. */
  hemShare: number;
  /** True when no split has been set, so the full benchmark applies. */
  isHemShareDefault: boolean;
  /** True when the (split) HEM benchmark is used instead of the stated household expenses. */
  isHemGreater: boolean;
  /** Non-rent living expenses used in the totals: either the split HEM or the stated expenses. */
  livingExpenses: number;
  /** Total monthly expenses: liabilities + living expenses + rent. */
  expenses: number;
  /** Income less expenses. */
  surplus: number;
}

/** An amount recorded against income, an expense or a liability, as a monthly figure. */
const toMonthlyAmount = (amount: unknown, frequency?: string | null): number =>
  toMonthlyFrequencyAmount(toFiniteNumber(amount), frequency);

export const clampFraction = (fraction: number): number => Math.min(1, Math.max(0, fraction));

/**
 * Rounds a 0-1 share to the two decimals the customer profile stores. The sliders step in 0.05, which MUI
 * computes as `min + step * Math.round(...)` and so hands back values such as 0.15000000000000002; rounding
 * here keeps a committed value comparing equal to the stored one instead of reading as an unsaved edit.
 */
export const toStoredShareFraction = (fraction: number): number => Math.round(fraction * 100) / 100;

/**
 * The HEM slider reads as the portion of the benchmark split off to someone else, so 0% leaves the whole
 * benchmark with this applicant. The profile stores the opposite — the share the applicant carries — which is
 * what shareOfHem is named for and what the PDF and Excel downloads print, so the two are converted at the
 * slider boundary. Both directions live here so the slider, its caption and the breakdown row agree.
 */
export const toHemShareCarried = (splitOff: number): number => toStoredShareFraction(1 - splitOff);
export const toHemSplitOff = (shareCarried: number): number => toStoredShareFraction(1 - shareCarried);

/** Converts a stored share percentage (0-100) into a fraction of 1, falling back to the full amount. */
export const toShareFraction = (percent: unknown, fallbackFraction = 1): number => {
  const parsed = parseFloat(String(percent));
  if (!Number.isFinite(parsed)) return fallbackFraction;
  return clampFraction(parsed / 100);
};

const isRent = (expense: ServiceabilityExpense): boolean => expense.expensesType === RENT_EXPENSE_TYPE;

/**
 * Monthly income for one item. An investment property is shaded down to the recorded percentage, so only
 * the portion a lender is willing to count reaches the total. Every other income type counts in full.
 */
export const toShadedMonthlyIncome = (item: ServiceabilityIncome): number => {
  const monthlyAmount = toMonthlyAmount(item.amount, item.frequency);
  const shadingPercent = toFiniteNumber(item.shading);

  if (item.incomeType !== INVESTMENT_PROPERTY_INCOME_TYPE || shadingPercent <= 0) return monthlyAmount;

  return monthlyAmount * (shadingPercent / 100);
};

/**
 * Monthly repayment for one liability before its share. A credit card adds a buffer taken as a percentage of
 * its limit, which is already a monthly figure and so is added after the repayment has been converted.
 */
export const toBufferedMonthlyRepayment = (liability: ServiceabilityLiability): number => {
  const monthlyRepayment = toMonthlyAmount(liability.repaymentAmount, liability.repaymentFrequency);
  const bufferPercent = toFiniteNumber(liability.buffer);

  if (liability.liabilitiesType !== CREDIT_CARD_LIABILITY_TYPE || bufferPercent <= 0) return monthlyRepayment;

  return monthlyRepayment + (toFiniteNumber(liability.limit) * bufferPercent) / 100;
};

/** Resolves the monthly HEM benchmark for the household size, extending the table for extra dependants. */
const getHemBenchmark = (
  hemConstant: (typeof HEM_CONSTANTS)[number],
  isMarried: boolean,
  dependants: number,
): number => {
  const baseRate = isMarried ? hemConstant.coupleRate : hemConstant.singleRate;
  const children = Math.max(0, Math.round(dependants));
  if (children === 0) return baseRate;

  const ratesByChildren = (isMarried ? hemConstant.coupleWithChildren : hemConstant.singleWithChildren) as Record<
    string,
    number
  >;
  const highestListedChildren = Object.keys(ratesByChildren)
    .map(Number)
    .reduce((highest, listed) => Math.max(highest, listed), 0);
  if (highestListedChildren === 0) return baseRate;

  if (children <= highestListedChildren) return ratesByChildren[String(children)] ?? baseRate;

  const additionalChildren = children - highestListedChildren;
  return ratesByChildren[String(highestListedChildren)] + additionalChildren * hemConstant.additionalChild;
};

/**
 * Monthly serviceability for a single customer.
 *
 * Living expenses are the higher of the customer's share of the HEM benchmark and their share of the stated
 * household expenses. The HEM share is set independently of the expense shares (spousal offset / HEM split);
 * when it is not set the full benchmark applies, so lowering the floor is always a deliberate decision.
 */
export const computeServiceability = ({
  income,
  expenses,
  liabilities,
  customerProfile,
  shareOfHem,
}: ServiceabilityInput): ServiceabilityResult => {
  const incomeList = income ?? [];
  const expensesList = expenses ?? [];
  const liabilitiesList = liabilities ?? [];

  const totalIncome = incomeList.reduce((total, item) => total + toShadedMonthlyIncome(item), 0);

  const totalLiabilities = liabilitiesList.reduce(
    (total, liability) => total + toBufferedMonthlyRepayment(liability) * toShareFraction(liability.shareOfLiability),
    0,
  );

  // A customer can hold more than one Rent row, and the stated expenses below exclude all of them, so they
  // are summed rather than taking the first: anything else drops the remaining rows from the calculation.
  const rentExpenses = expensesList.filter(isRent);
  const rent = rentExpenses.reduce(
    (total, expense) =>
      total + toMonthlyAmount(expense.amount, expense.frequency) * toShareFraction(expense.shareOfExpense),
    0,
  );

  const nonRentExpenses = expensesList.filter((expense) => !isRent(expense));
  const statedHouseholdExpenses = nonRentExpenses.reduce(
    (total, expense) =>
      total + toMonthlyAmount(expense.amount, expense.frequency) * toShareFraction(expense.shareOfExpense),
    0,
  );

  // The benchmark is a floor on living expenses, so an unset split carries all of it. The expense shares answer
  // "who pays this bill" rather than "whose benchmark is this", and must not quietly lower the floor.
  // Number("") is 0 and finite, so a profile field left as an empty string has to be caught alongside null:
  // read as an explicit 0% carried share it would drop the whole benchmark and overstate the surplus.
  const isHemShareDefault = shareOfHem == null || shareOfHem === "" || !Number.isFinite(Number(shareOfHem));
  const hemShare = isHemShareDefault ? 1 : clampFraction(Number(shareOfHem));

  // The HEM bands are contiguous only on a 0.01 grid (one ends at 1975.6, the next starts at 1975.61), so the
  // income is rounded to cents before the lookup. A fraction of a cent — which any non-monthly frequency produces —
  // would otherwise fall between two bands, resolve to no benchmark and silently drop the living expense floor.
  const bandIncome = Math.round(totalIncome * 100) / 100;
  const hemConstant = HEM_CONSTANTS.find((hem) => hem.minIncome <= bandIncome && hem.maxIncome >= bandIncome);
  const isMarried = customerProfile?.maritalStatus === MARITAL_STATUSES.MARRIED_DEFACTO;
  const hemBenchmark =
    hemConstant && customerProfile?.maritalStatus
      ? getHemBenchmark(hemConstant, isMarried, toFiniteNumber(customerProfile?.numberOfDependants))
      : null;

  const hemApplied = hemBenchmark === null ? null : hemBenchmark * hemShare;
  const isHemGreater = hemApplied !== null && hemApplied >= statedHouseholdExpenses;
  const livingExpenses = isHemGreater && hemApplied !== null ? hemApplied : statedHouseholdExpenses;

  const totalExpenses = totalLiabilities + livingExpenses + rent;

  return {
    income: totalIncome,
    liabilities: totalLiabilities,
    rent,
    statedHouseholdExpenses,
    hemBenchmark,
    hemApplied,
    hemShare,
    isHemShareDefault,
    isHemGreater,
    livingExpenses,
    expenses: totalExpenses,
    surplus: totalIncome - totalExpenses,
  };
};
